import React, { useEffect, useMemo, useState } from "react";
import moment from "moment";
import { useDispatch } from "react-redux";
import { PrimaryButton } from "@/components/Buttons.jsx";
import ToggleSwitch from "@/components/ToggleSwitch.jsx";
import TimePicker from "react-time-picker";
import "react-time-picker/dist/TimePicker.css";
import "./picker.css";
import { updateStoreHours } from "@/api/index.js";
import { addToast, fetchOnboardSteps} from "@/store/app/action.js";

const daysInOrder = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
const daysToLongForm = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

const RegularHours = ({ tzOffset, storeDetails }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [amDirty, setAmDirty] = useState(false);
  const [hours, setHours] = useState({});
  const [normalizedHours, setNormalizedHours] = useState([]);

  useEffect(() => {
    if (storeDetails) {
      setHours(storeDetails.hours);
    }
  }, [storeDetails]);

  useEffect(() => {
    if (hours) {
      let hoursInArray = [];
      daysInOrder.forEach((day, idx) => {
        hoursInArray[idx] = (hours[day] || "").replace(" ", "");
      });

      hoursInArray.forEach((todayHours, idx) => {
        todayHours = todayHours.split(",");
        let idxToRemove = -1;
        todayHours.forEach((oneBlock, idx2) => {
          const hourSplit = oneBlock.split("-");
          const open = hourSplit[0];
          const close = hourSplit[1];

          if (open === "0000" && todayHours.length > 1) {
            let mergeWithIndex = idx - 1;
            if (mergeWithIndex === -1) {
              mergeWithIndex = 6;
            }

            const hoursToBeMerged = hoursInArray[mergeWithIndex].split(",");
            hoursToBeMerged.forEach((hourBlockToBeMerged, idx3) => {
              const hourSplit = hourBlockToBeMerged.split("-");
              let toBeMergedClose = hourSplit[1];
              if (toBeMergedClose === "2400") {
                hoursToBeMerged[idx3] = [hourSplit[0], close].join("-");
                hoursInArray[mergeWithIndex] = hoursToBeMerged.join(",");
              }
            });
            idxToRemove = idx2;
          }
        });
        if (idxToRemove !== -1) {
          todayHours.splice(idxToRemove, 1);
        }
        hoursInArray[idx] = todayHours[0];
      });
      for (let i = 0; i < 7; i++) {
        if (
          hoursInArray[i] === undefined ||
          hoursInArray[i] === "" ||
          hoursInArray[i] === "0000-0000"
        ) {
          hoursInArray[i] = {
            open: false,
            opensAt: "12:00am",
            closesAt: "12:00am",
            closesNextDay: true,
            allDay: false,
          };
        } else {
          const hourSplit = hoursInArray[i].split("-");
          const opensAt = moment(hourSplit[0], "HHmm")
            .add(tzOffset * -1, "minutes")
            .format("HH:mm");
          const closesAt = moment(hourSplit[1], "HHmm")
            .add(tzOffset * -1, "minutes")
            .format("HH:mm");
          const opensLocal = moment(hourSplit[0], "HHmm");
          const closesLocal = moment(hourSplit[1], "HHmm");
          const closesNextDay = opensLocal.isAfter(closesLocal);

          hoursInArray[i] = {
            open: true,
            opensAt,
            closesAt,
            closesNextDay,
            allDay: opensAt === closesAt,
          };
        }
      }
      setNormalizedHours(hoursInArray);
    }
  }, [JSON.stringify(hours)]);

  const save = async () => {
    let toSubmit = {};
    normalizedHours.forEach((hours, idx) => {
      if (!toSubmit[daysInOrder[idx]]) {
        toSubmit[daysInOrder[idx]] = [];
      }
      if (!hours.open) {
        return;
      }
      if (hours.allDay) {
        toSubmit[daysInOrder[idx]].push("0000-2400");
      } else {
        const opensat = moment(hours.opensAt, "HH:mm")
          .add(tzOffset, "minutes")
          .format("HHmm")
        const closesat = moment(hours.closesAt, "HH:mm")
          .add(tzOffset, "minutes")
          .format("HHmm")

        if (closesat < opensat) {
          toSubmit[daysInOrder[idx]].push(
            `${moment(hours.opensAt, "HH:mm")
              .add(tzOffset, "minutes")
              .format("HHmm")}-2400`
          );
          let nextDay = idx === 6 ? 0 : idx + 1;
          if (!toSubmit[daysInOrder[nextDay]]) {
            toSubmit[daysInOrder[nextDay]] = [];
          }
          toSubmit[daysInOrder[nextDay]].unshift(
            `0000-${moment(hours.closesAt, "HH:mm")
              .add(tzOffset, "minutes")
              .format("HHmm")}`
          );
        } else {
          toSubmit[daysInOrder[idx]].push(
            `${moment(hours.opensAt, "HH:mm")
              .add(tzOffset, "minutes")
              .format("HHmm")}-${moment(hours.closesAt, "HH:mm")
              .add(tzOffset, "minutes")
              .format("HHmm")}`
          );
        }
      }
    });
    Object.keys(toSubmit).forEach((key) => {
      toSubmit[key] = toSubmit[key].join(",");
    });
    setLoading(true);
    try {
      await updateStoreHours({ hours: toSubmit });
      setAmDirty(false);
      dispatch(
        addToast({
          message: "Store hours updated successfully",
          type: "success",
          timer: 5,
        })
      );
    } catch (e) {
      dispatch(
        addToast({
          message: "Error updating store hours, please try again later",
          type: "error",
          timer: 5,
        })
      );
    } finally {
      dispatch(fetchOnboardSteps());
      setLoading(false);
    }
  };

  const changeOpenFlag = (idx, event) => {
    let hoursCloned = JSON.parse(JSON.stringify(normalizedHours));
    hoursCloned[idx].open = event;
    setNormalizedHours(hoursCloned);
    setAmDirty(true);
  };
  const changeTime = (idx, which, event) => {
    let hoursCloned = JSON.parse(JSON.stringify(normalizedHours));
    hoursCloned[idx][which] = event;

    const opensLocal = moment(hoursCloned[idx].opensAt, "HH:mm");
    const closesLocal = moment(hoursCloned[idx].closesAt, "HH:mm");
    hoursCloned[idx].closesNextDay = opensLocal.isAfter(closesLocal);

    hoursCloned[idx].allDay =
      hoursCloned[idx].opensAt === hoursCloned[idx].closesAt;
    setNormalizedHours(hoursCloned);
    setAmDirty(true);
  };

  const changeAllDayFlag = (idx, event) => {
    let hoursCloned = JSON.parse(JSON.stringify(normalizedHours));
    hoursCloned[idx].allDay = event;
    setNormalizedHours(hoursCloned);
    setAmDirty(true);
  };

  return (
    <div className="grow">
      <div className="p-6 space-y-6">
        <h2 className="text-xl text-slate-800 dark:text-slate-100 font-bold mb-5">
          Regular Store Hours
        </h2>
        {[0, 1, 2, 3, 4, 5, 6].map((idx) => {
          if (!normalizedHours[idx]) return null;
          return (
            <div
              key={`regular_hour_${idx}`}
              className={"border rounded my-2 p-4"}
            >
              <div className={"flex w-full justify-center items-center"}>
                <div className={"flex gap-4"}>
                  <div className={"flex gap-4"}>
                    <h5 style={{ width: 100 }}>{daysToLongForm[idx]}</h5>{" "}
                    <ToggleSwitch
                      checked={normalizedHours[idx].open}
                      onChange={(e) => changeOpenFlag(idx, e)}
                      offLabel="Open"
                      onLabel="Open"
                    />
                    {normalizedHours[idx].open && (
                      <ToggleSwitch
                        checked={normalizedHours[idx].allDay}
                        onChange={(e) => changeAllDayFlag(idx, e)}
                        onLabel="24 Hours"
                        offLabel="24 Hours"
                      />
                    )}
                  </div>
                </div>
                <div className={"flex flex-1 justify-end"}>
                  {normalizedHours[idx].open &&
                    !normalizedHours[idx].allDay && (
                      <div className="flex items-center gap-4">
                        <div className="w-48 flex flex-col ">
                          <label
                            className="text-sm font-medium mb-1"
                            htmlFor="name"
                          >
                            Opens at ({storeDetails?.tz})
                          </label>
                          <TimePicker
                            disabled={normalizedHours[idx].allDay}
                            value={normalizedHours[idx].opensAt}
                            onChange={(e) => changeTime(idx, "opensAt", e)}
                            clockIcon={null}
                            disableClock={true}
                            clearIcon={null}
                          />
                          {/*<TimeInput*/}
                          {/*  disabled={normalizedHours[idx].allDay}*/}
                          {/*  initTime={normalizedHours[idx].opensAt}*/}
                          {/*  onTimeChange={(e) => changeTime(idx, "opensAt", e)}*/}
                          {/*/>*/}
                        </div>

                        {"-"}

                        <div className="w-48 flex flex-col">
                          <label
                            className="text-sm font-medium mb-1"
                            htmlFor="name"
                          >
                            {`Closes at${
                              normalizedHours[idx].closesNextDay
                                ? " (Next Day)"
                                : ""
                            }`}{" "}
                            ({storeDetails?.tz})
                          </label>
                          <TimePicker
                            disabled={normalizedHours[idx].allDay}
                            value={normalizedHours[idx].closesAt}
                            onChange={(e) => changeTime(idx, "closesAt", e)}
                            clockIcon={null}
                            disableClock={true}
                            clearIcon={null}
                          />
                        </div>

                        {/*<TextField*/}
                        {/*  disabled={normalizedHours[idx].allDay}*/}
                        {/*  id="time"*/}
                        {/*  label="Opens at"*/}
                        {/*  type="time"*/}
                        {/*  value={normalizedHours[idx].opensAt}*/}
                        {/*  InputLabelProps={{*/}
                        {/*    shrink: true,*/}
                        {/*  }}*/}
                        {/*  style={{ width: 150 }}*/}
                        {/*  onChange={(e) => changeTime(idx, "opensAt", e)}*/}
                        {/*/>*/}
                        {/*{"-"}*/}
                        {/*<TextField*/}
                        {/*  disabled={normalizedHours[idx].allDay}*/}
                        {/*  id="time"*/}
                        {/*  label={`Closes at${*/}
                        {/*    normalizedHours[idx].closesNextDay ? " (Next Day)" : ""*/}
                        {/*  }`}*/}
                        {/*  type="time"*/}
                        {/*  value={*/}
                        {/*    normalizedHours[idx].allDay*/}
                        {/*      ? normalizedHours[idx].opensAt*/}
                        {/*      : normalizedHours[idx].closesAt*/}
                        {/*  }*/}
                        {/*  InputLabelProps={{*/}
                        {/*    shrink: true,*/}
                        {/*  }}*/}
                        {/*  style={{ width: 150 }}*/}
                        {/*  onChange={(e) => changeTime(idx, "closesAt", e)}*/}
                        {/*/>*/}
                      </div>
                    )}
                </div>
              </div>
            </div>
          );
        })}
        <div>
          <PrimaryButton
            disabled={!amDirty || loading}
            loading={loading}
            onClick={save}
            color="primary"
          >
            Save Changes
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
};

export default RegularHours;
